import { CircularProgress } from '@material-ui/core'
import { Button } from 'components/facets'
import { handleFetchOrders } from 'lib/api'
import { useOrderStore } from 'lib/hooks'
import { useFetchOrders } from 'lib/hooks/useFetchOrders'
import { useSnackbar } from 'notistack'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ORDER_SIDEBAR_TABS } from 'shared'
import { InventoryTransactionsPresenter } from '../inventory/InventoryTransactionsPresenter'
import '../orders/orders-presenter.css'
import { OrdersTable } from '../orders/OrdersTable'
import { OrdersToolbar } from '../orders/OrdersToolbar'
import { UserStockSearch } from './UserStockSearch'

const TABS = ['Stock', 'Orders', 'Transactions'] as const

const noop = () => undefined

// The user's Fulfilment Center, without the actions that change orders
const UserOrders = ({ userId }: { userId: string }): JSX.Element => {
  const { t } = useTranslation()
  const { enqueueSnackbar } = useSnackbar()
  const { convertOrderResponseDtoToOrderClass } = useFetchOrders()
  const [loading, setLoading] = useState(false)
  const [hasLoaded, setHasLoaded] = useState(false)

  const loadOrders = async (): Promise<void> => {
    // Without a user the endpoints answer with the logged-in account's own data: never ask them
    if (!userId) {
      enqueueSnackbar('select a user first', { variant: 'error' })
      return
    }
    setLoading(true)
    try {
      const { setActiveTab, setOrders } = useOrderStore.getState()
      setActiveTab(ORDER_SIDEBAR_TABS.FULFILMENT_CENTER)
      const dtos = await handleFetchOrders(userId)
      setOrders((dtos ?? []).map(convertOrderResponseDtoToOrderClass))
      setHasLoaded(true)
    } catch (error) {
      enqueueSnackbar('Error loading orders', { variant: 'error' })
    }
    setLoading(false)
  }

  return (
    <>
      <Button variant="contained" className="mb-4" onClick={loadOrders} disabled={loading}>
        {loading ? <CircularProgress size={16} /> : t('backoffice.loadOrders')}
      </Button>
      {hasLoaded && !loading && (
        <>
          <div className="mb-5">
            <OrdersToolbar handleUpdateOrders={noop} viewOnly />
          </div>
          <OrdersTable handleUpdateOrders={noop} handleChangeOrderState={noop} viewOnly />
        </>
      )}
    </>
  )
}

export const UserImpersonation = ({ userId }: { userId: string }): JSX.Element => {
  const [tab, setTab] = useState(0)

  // Every tab below reads through the user's own endpoints, which fall back to the logged-in
  // account when no user is passed: without a chosen user nothing is mounted, so nothing is asked
  if (!userId) return <></>

  return (
    <div className="mt-4">
      <ul className="nav nav-tabs">
        {TABS.map((l, i) => (
          <li key={l} className="nav-item">
            <a
              className={`nav-link${tab === i ? ' active' : ''}`}
              href="#"
              onClick={(e) => {
                e.preventDefault()
                setTab(i)
              }}
            >
              {l}
            </a>
          </li>
        ))}
      </ul>
      <div className="backoffice-user-impersonification-panel">
        {tab === 0 && <UserStockSearch key={userId} userId={userId} />}
        {tab === 1 && <UserOrders key={userId} userId={userId} />}
        {tab === 2 && <InventoryTransactionsPresenter key={userId} userId={userId} />}
      </div>
    </div>
  )
}
